import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  IconButton as ListIconButton,
  useTheme,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  StepConnector,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Psychology as PsychologyIcon,
  Info as InfoIcon,
  Settings as SettingsIcon,
  EditNote as EditNoteIcon,
  DoneAll as DoneAllIcon,
  AutoAwesome as AutoAwesomeIcon,
  OpenInFull as OpenInFullIcon,
  CloseFullscreen as CloseFullscreenIcon,
  AutoFixHigh as AutoFixHighIcon,
  SupportAgent as SupportAgentIcon,
  RocketLaunch as RocketLaunchIcon,
  Campaign as CampaignIcon,
  HeadsetMic as HeadsetMicIcon,
  ShoppingCart as ShoppingCartIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  Engineering as EngineeringIcon,
  AccountBalance as AccountBalanceIcon,
  Restaurant as RestaurantIcon,
  Home as HomeIcon,
  DirectionsCar as DirectionsCarIcon,
  Computer as ComputerIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  Forum as ForumIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Help as HelpIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  Favorite as FavoriteIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Build as BuildIcon,
  Tune as TuneIcon,
  Palette as PaletteIcon,
  Brush as BrushIcon,
  Create as CreateIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  RestartAlt as RestartAltIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  SkipNext as SkipNextIcon,
  SkipPrevious as SkipPreviousIcon,
  FastForward as FastForwardIcon,
  FastRewind as FastRewindIcon,
  VolumeUp as VolumeUpIcon,
  VolumeDown as VolumeDownIcon,
  VolumeOff as VolumeOffIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  CameraAlt as CameraAltIcon,
  PhotoCamera as PhotoCameraIcon,
  Image as ImageIcon,
  VideoLibrary as VideoLibraryIcon,
  MusicNote as MusicNoteIcon,
  LibraryMusic as LibraryMusicIcon,
  Movie as MovieIcon,
  Theaters as TheatersIcon,
  Sports as SportsIcon,
  FitnessCenter as FitnessCenterIcon,
  Pool as PoolIcon,
  DirectionsRun as DirectionsRunIcon,
  DirectionsBike as DirectionsBikeIcon,
  DirectionsWalk as DirectionsWalkIcon,
  Flight as FlightIcon,
  Train as TrainIcon,
  DirectionsBus as DirectionsBusIcon,
  LocalTaxi as LocalTaxiIcon,
  Hotel as HotelIcon,
  LocationOn as LocationOnIcon,
  MyLocation as MyLocationIcon,
  Navigation as NavigationIcon,
  Map as MapIcon,
  Public as PublicIcon,
  Language as LanguageIcon,
  Translate as TranslateIcon,
  GTranslate as GTranslateIcon,
  Menu as MenuIcon,
  MoreVert as MoreVertIcon,
  MoreHoriz as MoreHorizIcon,
  Apps as AppsIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  ViewComfy as ViewComfyIcon,
  ViewStream as ViewStreamIcon,
  ViewColumn as ViewColumnIcon,
  ViewWeek as ViewWeekIcon,
  ViewDay as ViewDayIcon,
  ViewAgenda as ViewAgendaIcon,
  ViewCarousel as ViewCarouselIcon,
  ViewQuilt as ViewQuiltIcon,
  ViewSidebar as ViewSidebarIcon,
  ViewInAr as ViewInArIcon,
  ViewArray as ViewArrayIcon,
  ViewCompact as ViewCompactIcon,
  ViewHeadline as ViewHeadlineIcon,
  ViewKanban as ViewKanbanIcon,
  ViewTimeline as ViewTimelineIcon,
} from '@mui/icons-material';
import { Template, CreateTemplateRequest } from '../../utils/templateApi';
import templateApi from '../../utils/templateApi';
import { AI_MODELS } from '../../constants/aiModels';
import FileManager from '../FileManager';
import toast from 'react-hot-toast';
import { styled } from '@mui/material/styles';
import TokenCounter from '../TokenCounter';
import { calculateTokenInfo } from '../../utils/tokenCounter';

type TemplateFormDialogProps = {
  open: boolean;
  onClose: () => void;
  onSaved: (template: Template) => void;
  template?: Template | null;
};

// Available icons for templates
const availableIcons = [
  { name: 'AutoAwesome', component: AutoAwesomeIcon, category: 'AI & Tech' },
  { name: 'SupportAgent', component: SupportAgentIcon, category: 'Support' },
  { name: 'RocketLaunch', component: RocketLaunchIcon, category: 'Marketing' },
  { name: 'Psychology', component: PsychologyIcon, category: 'AI & Tech' },
  { name: 'Campaign', component: CampaignIcon, category: 'Marketing' },
  { name: 'HeadsetMic', component: HeadsetMicIcon, category: 'Support' },
  { name: 'ShoppingCart', component: ShoppingCartIcon, category: 'E-commerce' },
  { name: 'Business', component: BusinessIcon, category: 'Business' },
  { name: 'School', component: SchoolIcon, category: 'Education' },
  { name: 'HealthAndSafety', component: HealthAndSafetyIcon, category: 'Health' },
  { name: 'Engineering', component: EngineeringIcon, category: 'Tech' },
  { name: 'AccountBalance', component: AccountBalanceIcon, category: 'Finance' },
  { name: 'Restaurant', component: RestaurantIcon, category: 'Food' },
  { name: 'Home', component: HomeIcon, category: 'General' },
  { name: 'DirectionsCar', component: DirectionsCarIcon, category: 'Transport' },
  { name: 'Computer', component: ComputerIcon, category: 'Tech' },
  { name: 'Phone', component: PhoneIcon, category: 'Communication' },
  { name: 'Email', component: EmailIcon, category: 'Communication' },
  { name: 'Chat', component: ChatIcon, category: 'Communication' },
  { name: 'Forum', component: ForumIcon, category: 'Communication' },
  { name: 'QuestionAnswer', component: QuestionAnswerIcon, category: 'Support' },
  { name: 'Help', component: HelpIcon, category: 'Support' },
  { name: 'Info', component: InfoIcon, category: 'General' },
  { name: 'Warning', component: WarningIcon, category: 'Alerts' },
  { name: 'Error', component: ErrorIcon, category: 'Alerts' },
  { name: 'CheckCircle', component: CheckCircleIcon, category: 'Status' },
  { name: 'Cancel', component: CancelIcon, category: 'Status' },
  { name: 'Star', component: StarIcon, category: 'General' },
  { name: 'Favorite', component: FavoriteIcon, category: 'General' },
  { name: 'ThumbUp', component: ThumbUpIcon, category: 'Feedback' },
  { name: 'ThumbDown', component: ThumbDownIcon, category: 'Feedback' },
  { name: 'Share', component: ShareIcon, category: 'Social' },
  { name: 'Download', component: DownloadIcon, category: 'Actions' },
  { name: 'Upload', component: UploadIcon, category: 'Actions' },
  { name: 'Settings', component: SettingsIcon, category: 'Configuration' },
  { name: 'Build', component: BuildIcon, category: 'Tools' },
  { name: 'Tune', component: TuneIcon, category: 'Configuration' },
  { name: 'Palette', component: PaletteIcon, category: 'Design' },
  { name: 'Brush', component: BrushIcon, category: 'Design' },
  { name: 'Create', component: CreateIcon, category: 'Actions' },
  { name: 'Edit', component: EditIcon, category: 'Actions' },
  { name: 'ContentCopy', component: ContentCopyIcon, category: 'Actions' },
  { name: 'Print', component: PrintIcon, category: 'Actions' },
  { name: 'Save', component: SaveIcon, category: 'Actions' },
  { name: 'CloudUpload', component: CloudUploadIcon, category: 'Cloud' },
  { name: 'CloudDownload', component: CloudDownloadIcon, category: 'Cloud' },
  { name: 'Sync', component: SyncIcon, category: 'Actions' },
  { name: 'Refresh', component: RefreshIcon, category: 'Actions' },
  { name: 'RestartAlt', component: RestartAltIcon, category: 'Actions' },
  { name: 'PlayArrow', component: PlayArrowIcon, category: 'Media' },
  { name: 'Pause', component: PauseIcon, category: 'Media' },
  { name: 'Stop', component: StopIcon, category: 'Media' },
  { name: 'SkipNext', component: SkipNextIcon, category: 'Media' },
  { name: 'SkipPrevious', component: SkipPreviousIcon, category: 'Media' },
  { name: 'FastForward', component: FastForwardIcon, category: 'Media' },
  { name: 'FastRewind', component: FastRewindIcon, category: 'Media' },
  { name: 'VolumeUp', component: VolumeUpIcon, category: 'Audio' },
  { name: 'VolumeDown', component: VolumeDownIcon, category: 'Audio' },
  { name: 'VolumeOff', component: VolumeOffIcon, category: 'Audio' },
  { name: 'Mic', component: MicIcon, category: 'Audio' },
  { name: 'MicOff', component: MicOffIcon, category: 'Audio' },
  { name: 'Videocam', component: VideocamIcon, category: 'Video' },
  { name: 'VideocamOff', component: VideocamOffIcon, category: 'Video' },
  { name: 'CameraAlt', component: CameraAltIcon, category: 'Camera' },
  { name: 'PhotoCamera', component: PhotoCameraIcon, category: 'Camera' },
  { name: 'Image', component: ImageIcon, category: 'Media' },
  { name: 'VideoLibrary', component: VideoLibraryIcon, category: 'Media' },
  { name: 'MusicNote', component: MusicNoteIcon, category: 'Audio' },
  { name: 'LibraryMusic', component: LibraryMusicIcon, category: 'Audio' },
  { name: 'Movie', component: MovieIcon, category: 'Media' },
  { name: 'Theaters', component: TheatersIcon, category: 'Media' },
  { name: 'Sports', component: SportsIcon, category: 'Sports' },
  { name: 'FitnessCenter', component: FitnessCenterIcon, category: 'Fitness' },
  { name: 'Pool', component: PoolIcon, category: 'Fitness' },
  { name: 'DirectionsRun', component: DirectionsRunIcon, category: 'Fitness' },
  { name: 'DirectionsBike', component: DirectionsBikeIcon, category: 'Fitness' },
  { name: 'DirectionsWalk', component: DirectionsWalkIcon, category: 'Fitness' },
  { name: 'Flight', component: FlightIcon, category: 'Travel' },
  { name: 'Train', component: TrainIcon, category: 'Travel' },
  { name: 'DirectionsBus', component: DirectionsBusIcon, category: 'Travel' },
  { name: 'LocalTaxi', component: LocalTaxiIcon, category: 'Travel' },
  { name: 'Hotel', component: HotelIcon, category: 'Travel' },
  { name: 'LocationOn', component: LocationOnIcon, category: 'Location' },
  { name: 'MyLocation', component: MyLocationIcon, category: 'Location' },
  { name: 'Navigation', component: NavigationIcon, category: 'Location' },
  { name: 'Map', component: MapIcon, category: 'Location' },
  { name: 'Public', component: PublicIcon, category: 'General' },
  { name: 'Language', component: LanguageIcon, category: 'Language' },
  { name: 'Translate', component: TranslateIcon, category: 'Language' },
  { name: 'GTranslate', component: GTranslateIcon, category: 'Language' },
  { name: 'Menu', component: MenuIcon, category: 'Navigation' },
  { name: 'MoreVert', component: MoreVertIcon, category: 'Navigation' },
  { name: 'MoreHoriz', component: MoreHorizIcon, category: 'Navigation' },
  { name: 'Apps', component: AppsIcon, category: 'Navigation' },
  { name: 'GridView', component: GridViewIcon, category: 'Views' },
  { name: 'ViewList', component: ViewListIcon, category: 'Views' },
  { name: 'ViewModule', component: ViewModuleIcon, category: 'Views' },
  { name: 'ViewComfy', component: ViewComfyIcon, category: 'Views' },
  { name: 'ViewStream', component: ViewStreamIcon, category: 'Views' },
  { name: 'ViewColumn', component: ViewColumnIcon, category: 'Views' },
  { name: 'ViewWeek', component: ViewWeekIcon, category: 'Views' },
  { name: 'ViewDay', component: ViewDayIcon, category: 'Views' },
  { name: 'ViewAgenda', component: ViewAgendaIcon, category: 'Views' },
  { name: 'ViewCarousel', component: ViewCarouselIcon, category: 'Views' },
  { name: 'ViewQuilt', component: ViewQuiltIcon, category: 'Views' },
  { name: 'ViewSidebar', component: ViewSidebarIcon, category: 'Views' },
  { name: 'ViewInAr', component: ViewInArIcon, category: 'Views' },
  { name: 'ViewArray', component: ViewArrayIcon, category: 'Views' },
  { name: 'ViewCompact', component: ViewCompactIcon, category: 'Views' },
  { name: 'ViewHeadline', component: ViewHeadlineIcon, category: 'Views' },
  { name: 'ViewKanban', component: ViewKanbanIcon, category: 'Views' },
  { name: 'ViewTimeline', component: ViewTimelineIcon, category: 'Views' },
];

const categories = Array.from(new Set(availableIcons.map(icon => icon.category)));

const TemplateFormDialog: React.FC<TemplateFormDialogProps> = ({
  open,
  onClose,
  onSaved,
  template
}) => {
  const theme = useTheme();
  const [saving, setSaving] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const isEditing = Boolean(template);
  const [formData, setFormData] = useState<CreateTemplateRequest>({
    name: '',
    description: '',
    type: 'Lead',
    model: 'gpt-4o-mini',
    status: 'Enabled',
    responseDelay: 0,
    prompts: [],
    category: '',
    icon: '',
    isDefault: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [iconSelectorOpen, setIconSelectorOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [promptDialog, setPromptDialog] = useState(false);
  const [currentPrompt, setCurrentPrompt] = useState({ role: 'assistant', content: '' });
  const [editingPromptIndex, setEditingPromptIndex] = useState<number | null>(null);
  const [expandedPrompts, setExpandedPrompts] = useState<Set<number>>(new Set());
  const [promptFilter, setPromptFilter] = useState('');

  // Configuração das steps
  const stepsConfig = [
    { label: 'Informações Básicas', icon: <InfoIcon /> },
    { label: 'Configuração', icon: <SettingsIcon /> },
    { label: 'Prompts', icon: <EditNoteIcon /> },
    { label: 'Revisão', icon: <DoneAllIcon /> },
  ] as const;

  // Connector estilizado
  const ColoredConnector = styled((props: any) => (
    <StepConnector {...props} />
  ))(({ theme }) => ({
    [`&.MuiStepConnector-alternativeLabel`]: {
      top: 16
    },
    [`& .MuiStepConnector-line`]: {
      height: 3,
      border: 0,
      borderRadius: 2,
      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[300]
    },
    [`&.Mui-active .MuiStepConnector-line`]: {
      background: theme.palette.text.primary
    },
    [`&.Mui-completed .MuiStepConnector-line`]: {
      background: theme.palette.text.primary
    }
  }));

  const types = ['Lead', 'Assistente', 'Ambos'];
  const statuses = ['Enabled', 'Disabled'];

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        description: template.description,
        type: template.type,
        model: template.model,
        status: template.status,
        responseDelay: template.responseDelay,
        prompts: template.prompts,
        category: template.category,
        icon: template.icon,
        isDefault: template.isDefault,
      });
    } else {
      setFormData({
        name: '',
        description: '',
        type: 'Lead',
        model: 'gpt-4o-mini',
        status: 'Enabled',
        responseDelay: 0,
        prompts: [],
        category: '',
        icon: '',
        isDefault: false,
      });
    }
    setErrors({});
  }, [template, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }

    if (!formData.category.trim()) {
      newErrors.category = 'Categoria é obrigatória';
    }

    if (formData.prompts.length === 0) {
      newErrors.prompts = 'Pelo menos um prompt é obrigatório';
    }

    if (formData.responseDelay < 0) {
      newErrors.responseDelay = 'Delay deve ser maior ou igual a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateFormWithoutStateUpdate = (): boolean => {
    if (!formData.name.trim()) return false;
    if (!formData.description.trim()) return false;
    if (!formData.category.trim()) return false;
    if (formData.prompts.length === 0) return false;
    if (formData.responseDelay < 0) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);

      let savedTemplate: Template;
      if (template) {
        savedTemplate = await templateApi.updateTemplate(template.id, formData);
        toast.success('Template atualizado com sucesso!');
      } else {
        savedTemplate = await templateApi.createTemplate(formData);
        toast.success('Template criado com sucesso!');
      }

      onSaved(savedTemplate);
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Erro ao salvar template');
    } finally {
      setSaving(false);
    }
  };

  const validateCurrentStep = (): boolean => {
    if (activeStep === 0) {
      if (!formData.name.trim()) {
        toast.error('Nome é obrigatório');
        return false;
      }
      if (!formData.description.trim()) {
        toast.error('Descrição é obrigatória');
        return false;
      }
      if (!formData.category.trim()) {
        toast.error('Categoria é obrigatória');
        return false;
      }
    }
    if (activeStep === 2) {
      if (formData.prompts.length === 0) {
        toast.error('Pelo menos um prompt é obrigatório');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;
    setActiveStep((prev) => Math.min(prev + 1, stepsConfig.length - 1));
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const canSaveForm = (): boolean => {
    return validateFormWithoutStateUpdate();
  };

  const handleAddPrompt = () => {
    setCurrentPrompt({ role: 'assistant', content: '' });
    setEditingPromptIndex(null);
    setPromptDialog(true);
  };

  const handleEditPrompt = (index: number) => {
    const prompt = formData.prompts![index];
    setCurrentPrompt({ ...prompt, role: 'assistant' });
    setEditingPromptIndex(index);
    setPromptDialog(true);
  };

  const handleSavePrompt = () => {
    if (!currentPrompt.content.trim()) {
      toast.error('Conteúdo do prompt é obrigatório');
      return;
    }

    const prompts = [...(formData.prompts || [])];
    const promptToSave = { ...currentPrompt, role: 'assistant' };

    if (editingPromptIndex !== null) {
      prompts[editingPromptIndex] = promptToSave;
    } else {
      prompts.push(promptToSave);
    }

    setFormData(prev => ({ ...prev, prompts }));
    setPromptDialog(false);
    setCurrentPrompt({ role: 'assistant', content: '' });
    setEditingPromptIndex(null);
  };

  const handleRemovePrompt = (index: number) => {
    setFormData(prev => ({
      ...prev,
      prompts: prev.prompts.filter((_, i) => i !== index)
    }));
  };

  const handleInputChange = (field: keyof CreateTemplateRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getSelectedIcon = () => {
    return availableIcons.find(icon => icon.name === formData.icon);
  };

  const filteredIcons = useMemo(() => 
    selectedCategory === 'All'
      ? availableIcons
      : availableIcons.filter(icon => icon.category === selectedCategory),
    [selectedCategory]
  );

  const allPromptsText = useMemo(() => 
    (formData.prompts || []).map(p => p.content).join(' '),
    [formData.prompts]
  );

  return (
    <Dialog 
      open={open} 
      onClose={() => { }} 
      maxWidth="lg" 
      fullWidth 
      disableEscapeKeyDown
      fullScreen={window.innerWidth < 768}
      sx={{
        '& .MuiDialog-paper': {
          height: window.innerWidth < 768 ? '100vh' : 'auto',
          margin: window.innerWidth < 768 ? 0 : 'auto',
          maxHeight: window.innerWidth < 768 ? '100vh' : '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        gap: 1,
        py: window.innerWidth < 768 ? 1.5 : 2
      }}>
        {template ? 'Editar Template' : 'Criar Template'}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        dividers 
        sx={{ 
          flex: 1,
          overflow: window.innerWidth < 768 ? 'hidden' : 'auto',
          display: 'flex',
          flexDirection: 'column',
          p: window.innerWidth < 768 ? 1 : 2
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Stepper */}
          <Paper elevation={3} sx={{ 
            p: { xs: 1, md: 3 }, 
            mb: { xs: 1, md: 3 }, 
            borderRadius: 2, 
            background: (theme) => `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`, 
            position: 'sticky', 
            top: 0, 
            zIndex: 9, 
            backdropFilter: 'saturate(140%) blur(6px)',
            overflow: { xs: 'hidden', md: 'visible' }
          }}>
            <Box 
              sx={{ 
                overflowX: { xs: 'auto', md: 'hidden' },
                overflowY: 'hidden',
                '&::-webkit-scrollbar': { 
                  height: 4,
                  display: { xs: 'block', md: 'none' }
                },
                '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 2 }
              }}
            >
              <Stepper
                activeStep={activeStep}
                alternativeLabel={window.innerWidth >= 768}
                connector={<ColoredConnector />}
                sx={{
                  minWidth: window.innerWidth < 768 ? `${stepsConfig.length * 120}px` : '100%',
                  '& .MuiStepLabel-label': { 
                    fontWeight: 500,
                    fontSize: window.innerWidth < 768 ? '0.75rem' : '0.875rem'
                  },
                  '& .MuiStepIcon-root.Mui-active': { 
                    color: window.innerWidth < 768 ? theme.palette.primary.main : 'text.primary' 
                  },
                  '& .MuiStepIcon-root.Mui-completed': { 
                    color: window.innerWidth < 768 ? theme.palette.primary.main : 'text.primary' 
                  },
                  '& .MuiStepLabel-root': {
                    minWidth: window.innerWidth < 768 ? '100px' : 'auto'
                  },
                  '& .MuiStepButton-root.Mui-active': {
                    backgroundColor: window.innerWidth < 768 ? `${theme.palette.primary.main}20` : 'transparent'
                  }
                }}
              >
                {stepsConfig.map((step, index) => {
                  const optional = (() => {
                    const chipSx = { 
                      height: 16, 
                      '& .MuiChip-label': { 
                        px: 0.5, 
                        lineHeight: '14px', 
                        fontSize: window.innerWidth < 768 ? 9 : 11 
                      } 
                    } as const;
                    if (step.label === 'Prompts') return <Chip color="primary" size="small" label={String(formData.prompts?.length || 0)} sx={chipSx} variant="outlined" />;
                    return undefined;
                  })();
                  const isActive = index === activeStep;
                  return (
                    <Step key={step.label}>
                      <StepButton
                        icon={step.icon}
                        optional={optional}
                        disabled={!isEditing && index > activeStep}
                        onClick={() => setActiveStep(index)}
                        sx={{
                          borderRadius: 2,
                          transition: 'background-color 120ms ease',
                          '& .MuiStepLabel-label': { 
                            fontWeight: isActive ? 700 : 500,
                            color: window.innerWidth < 768 && isActive ? theme.palette.primary.main : 'inherit'
                          },
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' },
                          minWidth: window.innerWidth < 768 ? '100px' : 'auto',
                          px: window.innerWidth < 768 ? 1 : 2,
                          backgroundColor: window.innerWidth < 768 && isActive ? `${theme.palette.primary.main}15` : 'transparent'
                        }}
                      >
                        {window.innerWidth < 768 ? (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: '0.7rem', 
                              textAlign: 'center', 
                              lineHeight: 1,
                              color: isActive ? theme.palette.primary.main : 'inherit',
                              fontWeight: isActive ? 700 : 500
                            }}
                          >
                            {step.label.split(' ')[0]}
                          </Typography>
                        ) : (
                          step.label
                        )}
                      </StepButton>
                    </Step>
                  );
                })}
              </Stepper>
            </Box>
          </Paper>


          <Box sx={{ 
            flex: 1, 
            overflow: window.innerWidth < 768 ? 'auto' : 'visible', 
            display: 'flex', 
            flexDirection: 'column' 
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 }, flex: 1 }}>
              {/* Step 0: Informações Básicas */}
              {activeStep === 0 && (
                <Paper elevation={2} sx={{ 
                  p: { xs: 2, md: 3 }, 
                  borderRadius: 2, 
                  transition: 'box-shadow 120ms ease, transform 120ms ease', 
                  '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' } 
                }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                    Informações Básicas
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Nome"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  fullWidth
                  required
                />

                <TextField
                  label="Descrição"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  error={!!errors.description}
                  helperText={errors.description}
                  fullWidth
                  multiline
                  rows={3}
                  required
                  sx={{
                    '& .MuiInputBase-inputMultiline': {
                      resize: 'vertical',
                      overflow: 'auto',
                    }
                  }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth error={!!errors.category}>
                    <InputLabel>Categoria</InputLabel>
                    <Select
                      value={formData.category}
                      onChange={(e) => handleInputChange('category', e.target.value)}
                      label="Categoria"
                    >
                      <MenuItem value="Qualificação">Qualificação</MenuItem>
                      <MenuItem value="Suporte">Suporte</MenuItem>
                      <MenuItem value="Campanha">Campanha</MenuItem>
                      <MenuItem value="Vendas">Vendas</MenuItem>
                      <MenuItem value="Atendimento">Atendimento</MenuItem>
                      <MenuItem value="Outros">Outros</MenuItem>
                    </Select>
                  </FormControl>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => setIconSelectorOpen(true)}
                      sx={{ 
                        minWidth: 'auto', 
                        px: 2,
                        fontSize: { xs: '0.75rem', md: '0.875rem' }
                      }}
                    >
                      {getSelectedIcon() ? (
                        React.createElement(getSelectedIcon()!.component, { fontSize: "small" })
                      ) : (
                        window.innerWidth < 768 ? 'Ícone' : 'Escolher'
                      )}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}

              {/* Step 1: Configuração */}
              {activeStep === 1 && (
                <Paper elevation={2} sx={{ 
                  p: { xs: 2, md: 3 }, 
                  borderRadius: 2, 
                  transition: 'box-shadow 120ms ease, transform 120ms ease', 
                  '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' } 
                }}>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                    Configuração
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ 
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: '1fr 1fr'
                      },
                      gap: { xs: 1.5, md: 2 }
                    }}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      value={formData.type}
                      onChange={(e) => handleInputChange('type', e.target.value)}
                      label="Tipo"
                    >
                      {types.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Modelo</InputLabel>
                    <Select
                      value={formData.model}
                      onChange={(e) => handleInputChange('model', e.target.value)}
                      label="Modelo"
                      sx={{
                        '& .MuiSelect-select': {
                          color: 'text.primary',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.4)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#F7F7F7',
                        },
                      }}
                    >
                      {AI_MODELS.map((model) => (
                        <MenuItem
                          key={model.value}
                          value={model.value}
                          sx={{
                            color: 'text.primary',
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 4,
                            '&:hover': {
                              backgroundColor: 'action.hover',
                            }
                          }}
                        >
                          <span style={{ flex: 1 }}>{model.label}</span>
                          <span style={{ color: 'text.secondary', marginLeft: '16px' }}>{model.credits} crédito{model.credits > 1 ? 's' : ''}</span>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                      label="Status"
                    >
                      {statuses.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                      <TextField
                        label="Delay de Resposta (ms)"
                        type="number"
                        value={formData.responseDelay}
                        onChange={(e) => handleInputChange('responseDelay', parseInt(e.target.value) || 0)}
                        error={!!errors.responseDelay}
                        helperText={errors.responseDelay}
                        fullWidth
                      />
                    </Box>
              </Box>
            </Paper>
          )}

          {/* Step 2: Prompts */}
          {activeStep === 2 && (
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, transition: 'box-shadow 120ms ease, transform 120ms ease', '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Prompts/Treinamentos ({formData.prompts?.length || 0})
                </Typography>
                <TextField
                  size="medium"
                  placeholder="Filtrar prompts..."
                  value={promptFilter}
                  onChange={(e) => setPromptFilter(e.target.value)}
                />
                <Button startIcon={<AddIcon />} onClick={handleAddPrompt} variant="outlined">Adicionar Prompt</Button>
              </Box>

              {/* Contador total de tokens dos prompts */}
              <Box sx={{
                mb: 2,
                p: 2,
                backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
                borderRadius: 1,
                border: '1px solid',
                borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'grey.200'
              }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Total de Tokens dos Prompts
                </Typography>
                <TokenCounter
                  text={allPromptsText}
                  maxTokens={200000}
                  showProgress={true}
                  showChip={false}
                  variant="default"
                />
              </Box>
              {(() => {
                const query = promptFilter.trim().toLowerCase();
                const promptsList = formData.prompts || [];
                const filtered = query
                  ? promptsList.reduce<{ prompt: any; originalIndex: number; }[]>((acc, p, i) => {
                    const role = (p.role || '').toLowerCase();
                    const content = (p.content || '').toLowerCase();
                    if (role.includes(query) || content.includes(query)) acc.push({ prompt: p, originalIndex: i });
                    return acc;
                  }, [])
                  : promptsList.map((p, i) => ({ prompt: p, originalIndex: i }));
                return filtered.map(({ prompt, originalIndex }) => {
                  const promptTokenInfo = calculateTokenInfo(prompt.content, 200000);
                  const isExpanded = expandedPrompts.has(originalIndex);
                  return (
                    <Box key={originalIndex} sx={{ 
                      border: 1, 
                      borderColor: 'divider', 
                      borderRadius: 2, 
                      p: { xs: 1.5, md: 2 }, 
                      mb: { xs: 1.5, md: 2 }, 
                      transition: 'box-shadow 120ms ease, transform 120ms ease, background-color 120ms ease', 
                      '&:hover': { boxShadow: 3, backgroundColor: 'action.hover', transform: 'translateY(-1px)' } 
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: { xs: 'center', sm: 'space-between' }, 
                        alignItems: { xs: 'center', sm: 'flex-start' }, 
                        mb: { xs: 0.5, md: 1 },
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 1, sm: 0 }
                      }}>
                        <Box sx={{ 
                          flexGrow: { xs: 0, sm: 1 }, 
                          minWidth: 0,
                          width: { xs: '100%', sm: 'auto' },
                          textAlign: { xs: 'center', sm: 'left' }
                        }}>
                          <Typography variant="body2" sx={{ 
                            whiteSpace: 'pre-wrap',
                            fontSize: { xs: '0.8rem', md: '0.875rem' },
                            lineHeight: { xs: 1.4, md: 1.5 }
                          }}>
                            {isExpanded ? (prompt.content || '') : (
                              (prompt.content || '').length > (window.innerWidth < 768 ? 100 : 200)
                                ? `${(prompt.content || '').substring(0, window.innerWidth < 768 ? 100 : 200)}...`
                                : (prompt.content || '')
                            )}
                          </Typography>
                        </Box>
                        <Box sx={{ 
                          display: 'flex', 
                          gap: { xs: 0.5, md: 1 },
                          flexWrap: 'wrap',
                          justifyContent: 'center',
                          width: { xs: '100%', sm: 'auto' }
                        }}>
                          <Tooltip title={isExpanded ? 'Recolher' : 'Expandir'}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => setExpandedPrompts(prev => {
                                  const next = new Set(prev);
                                  if (next.has(originalIndex)) next.delete(originalIndex); else next.add(originalIndex);
                                  return next;
                                })}
                              >
                                {isExpanded ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <IconButton size="small" onClick={() => handleEditPrompt(originalIndex)}>
                            <AddIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleRemovePrompt(originalIndex)} color="error">
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: { xs: 'center', sm: 'space-between' }, 
                        alignItems: 'center',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 0.5, sm: 0 }
                      }}>
                        <Typography
                          variant="caption"
                          color={promptTokenInfo.isOverLimit ? 'error' : 'text.secondary'}
                          sx={{ 
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            textAlign: { xs: 'center', sm: 'left' }
                          }}
                        >
                          {promptTokenInfo.characters} caracteres | {promptTokenInfo.tokens} tokens
                        </Typography>
                        {promptTokenInfo.isOverLimit && (
                          <Typography 
                            variant="caption" 
                            color="error" 
                            sx={{ 
                              fontSize: { xs: '0.7rem', md: '0.75rem' }, 
                              fontWeight: 'bold',
                              textAlign: { xs: 'center', sm: 'left' }
                            }}
                          >
                            Limite excedido
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                });
              })()}
            </Paper>
          )}

              {/* Step 3: Revisão */}
              {activeStep === 3 && (
                <Paper elevation={2} sx={{ 
                  p: { xs: 2, md: 3 }, 
                  borderRadius: 2, 
                  transition: 'box-shadow 120ms ease, transform 120ms ease', 
                  '&:hover': { boxShadow: 6, transform: 'translateY(-2px)' } 
                }}>
                  <Typography variant="h6" gutterBottom sx={{ m: 0, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                    Revisão
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 1.5, md: 2 }, fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                    Revise os dados antes de salvar.
                  </Typography>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(2, 1fr)' }, 
                    gap: { xs: 1.5, md: 2 } 
                  }}>
                <TextField label="Nome" value={formData.name || ''} InputProps={{ readOnly: true }} />
                <TextField label="Categoria" value={formData.category || ''} InputProps={{ readOnly: true }} />
                <TextField label="Tipo" value={formData.type || ''} InputProps={{ readOnly: true }} />
                <TextField label="Modelo" value={formData.model || ''} InputProps={{ readOnly: true }} />
                <TextField label="Status" value={formData.status || ''} InputProps={{ readOnly: true }} />
                <TextField label="Delay (ms)" value={String(formData.responseDelay || 0)} InputProps={{ readOnly: true }} />
                <TextField label="Prompts" value={String(formData.prompts?.length || 0)} InputProps={{ readOnly: true }} />
                <TextField label="Ícone" value={formData.icon || 'Nenhum'} InputProps={{ readOnly: true }} />
              </Box>
            </Paper>
          )}

              {/* Botões de navegação e ação */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between', 
                gap: { xs: 2, md: 2 },
                mt: 'auto',
                pt: { xs: 2, md: 3 }
              }}>
                {/* Botão Cancelar */}
                <Button 
                  variant="outlined" 
                  onClick={onClose} 
                  disabled={saving} 
                  startIcon={<CloseIcon />} 
                  sx={{ 
                    transition: 'transform 120ms ease', 
                    '&:hover': { transform: 'translateY(-1px)' },
                    order: { xs: 3, md: 1 }
                  }}
                >
                  Cancelar
                </Button>
                
                {/* Botões de navegação (setas) */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 1, md: 2 },
                  order: { xs: 1, md: 2 },
                  justifyContent: { xs: 'center', md: 'flex-start' }
                }}>
                  <Button 
                    variant="outlined" 
                    onClick={handleBack} 
                    disabled={activeStep === 0 || saving} 
                    sx={{ 
                      transition: 'transform 120ms ease', 
                      '&:hover': { transform: 'translateY(-1px)' },
                      minWidth: { xs: '48px', md: 'auto' },
                      px: { xs: 1, md: 2 }
                    }}
                  >
                    <ArrowBackIcon />
                    {window.innerWidth >= 768 && <span style={{ marginLeft: '8px' }}>Voltar</span>}
                  </Button>
                  
                  {activeStep < stepsConfig.length - 1 ? (
                    <Button 
                      variant="contained" 
                      onClick={handleNext} 
                      sx={{ 
                        color: theme.palette.mode === 'dark' ? '#000' : undefined, 
                        transition: 'transform 120ms ease, box-shadow 120ms ease', 
                        '&:hover': { boxShadow: 6, transform: 'translateY(-1px)' },
                        minWidth: { xs: '48px', md: 'auto' },
                        px: { xs: 1, md: 2 }
                      }}
                    >
                      {window.innerWidth >= 768 && <span style={{ marginRight: '8px' }}>Próximo</span>}
                      <ArrowForwardIcon />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                      disabled={saving || !canSaveForm()}
                      sx={{
                        color: theme.palette.mode === 'dark' ? '#000' : undefined,
                        transition: 'transform 120ms ease, box-shadow 120ms ease',
                        '&:hover': { boxShadow: 6, transform: 'translateY(-1px)' },
                        '&:disabled': {
                          backgroundColor: 'grey.400',
                          color: 'grey.600'
                        },
                        minWidth: { xs: 'auto', md: 'auto' },
                        px: { xs: 2, md: 2 }
                      }}
                    >
                      {saving ? 'Salvando...' : (template ? 'Atualizar' : 'Criar Template')}
                    </Button>
                  )}
                </Box>
                
                {/* Botão Salvar (apenas quando editando e não na última step) */}
                {isEditing && activeStep < stepsConfig.length - 1 && (
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={saving || !canSaveForm()}
                    sx={{
                      color: theme.palette.mode === 'dark' ? '#000' : undefined,
                      transition: 'transform 120ms ease, box-shadow 120ms ease',
                      '&:hover': { boxShadow: 6, transform: 'translateY(-1px)' },
                      '&:disabled': {
                        backgroundColor: 'grey.400',
                        color: 'grey.600'
                      },
                      order: { xs: 2, md: 3 }
                    }}
                  >
                    {saving ? 'Salvando...' : 'Salvar'}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* Dialog para editar prompt */}
      <Dialog open={promptDialog} onClose={() => setPromptDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPromptIndex !== null ? 'Editar Prompt' : 'Novo Prompt'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={8}
            sx={{
              mt: 2,
              '& .MuiInputBase-inputMultiline': {
                resize: 'vertical',
                overflow: 'auto',
              }
            }}
            label="Conteúdo do Prompt"
            value={currentPrompt.content}
            onChange={(e) => setCurrentPrompt(prev => ({ ...prev, content: e.target.value }))}
            placeholder="Digite o conteúdo do prompt aqui..."
            error={(() => {
              const totalText = allPromptsText + ' ' + (currentPrompt.content || '');
              const totalTokens = Math.ceil(totalText.length / 4);
              return totalTokens > 200000;
            })()}
            helperText={(() => {
              const totalText = allPromptsText + ' ' + (currentPrompt.content || '');
              const totalTokens = Math.ceil(totalText.length / 4);
              if (totalTokens > 200000) {
                return 'Limite total de tokens excedido. Reduza o conteúdo do prompt.';
              }
              return '';
            })()}
          />
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Total de Tokens (todos os prompts + atual)
            </Typography>
            <TokenCounter
              text={allPromptsText + ' ' + (currentPrompt.content || '')}
              maxTokens={200000}
              showProgress={true}
              showChip={true}
            />
          </Box>
          <FileManager
            promptText={currentPrompt.content || ''}
            onTextChange={(newText) => setCurrentPrompt(prev => ({ ...prev, content: newText }))}
            disabled={false}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromptDialog(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSavePrompt} variant="contained" sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Icon Selector Dialog */}
      <Dialog open={iconSelectorOpen} onClose={() => setIconSelectorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Escolher Ícone
          <IconButton
            onClick={() => setIconSelectorOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                label="Categoria"
              >
                <MenuItem value="All">Todas as categorias</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
            gap: 1,
            maxHeight: 400,
            overflow: 'auto'
          }}>
            {filteredIcons.map((icon) => (
              <Button
                key={icon.name}
                variant={formData.icon === icon.name ? 'contained' : 'outlined'}
                onClick={() => {
                  handleInputChange('icon', icon.name);
                  setIconSelectorOpen(false);
                }}
                sx={{
                  minWidth: 'auto',
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  color: formData.icon === icon.name ? (theme.palette.mode === 'dark' ? '#000' : undefined) : undefined
                }}
              >
                {React.createElement(icon.component, {
                  fontSize: "small",
                  sx: formData.icon === icon.name ? { color: theme.palette.mode === 'dark' ? '#000' : undefined } : undefined
                })}
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.7rem',
                    textAlign: 'center',
                    color: formData.icon === icon.name ? (theme.palette.mode === 'dark' ? '#000' : undefined) : undefined
                  }}
                >
                  {icon.name}
                </Typography>
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIconSelectorOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default TemplateFormDialog;
